import { Component, ElementRef, HostListener, NgZone, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription, filter, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { State } from 'src/app/core/store';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import { LittleTiffService } from 'src/app/core/services/little-tiff.service';
import { AppPermission } from 'src/app/models/appEnums';
import {
  LittleTiffActionLinkModel,
  LittleTiffAskResponseModel,
  LittleTiffStatusModel
} from 'src/app/models/littleTiffModel';

interface LittleTiffFeedbackContext {
  question: string;
  currentPath: string;
  pageTitle: string;
  contextJson: string;
}

interface LittleTiffMessage {
  role: 'user' | 'assistant';
  text: string;
  steps?: string[];
  actions?: LittleTiffActionLinkModel[];
  warnings?: string[];
  modelId?: string;
  sourcesUsed?: string[];
  feedback?: LittleTiffFeedbackContext | null;
  feedbackSaved?: boolean;
  showCorrection?: boolean;
  correction?: string;
  feedbackStatus?: string;
}

const MAX_STORED_MESSAGES = 40;
const MAX_API_HISTORY = 6;
const MAX_SNAPSHOT_ITEMS = 40;
const MAX_SNAPSHOT_ITEM_LENGTH = 80;
const DRAG_THRESHOLD_PX = 5;
const VIEWPORT_MARGIN_PX = 8;
const PANEL_WIDTH_PX = 380;
const PANEL_HEIGHT_PX = 520;
// Little Tiff sits just left of the first of these header buttons until the user drags it somewhere else.
const DEFAULT_ANCHOR_SELECTORS = ['#managementButton', '.mainMenuButton'];

@Component({
  selector: 'app-little-tiff',
  templateUrl: './little-tiff.component.html',
  styleUrls: ['./little-tiff.component.scss'],
  standalone: false
})
export class LittleTiffComponent extends BaseComponent {
  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('root') root?: ElementRef<HTMLDivElement>;

  canUse = false;
  isOpen = false;
  isLoading = false;
  statusRequested = false;
  status: LittleTiffStatusModel | null = null;
  statusMessage = '';
  selectedModelId = '';
  input = '';
  messages: LittleTiffMessage[] = [];
  left: number | null = null;
  top: number | null = null;
  panelBelow = true;
  panelMaxHeight = PANEL_HEIGHT_PX;
  panelAlignLeft = false;
  isDragging = false;

  private drag: { pointerId: number; startX: number; startY: number; offsetX: number; offsetY: number } | null = null;
  private suppressNextClick = false;
  private savedPosition: { left: number; top: number } | null = null;
  private userChanged = new Subject<void>();
  private anchorObserver?: ResizeObserver;
  private watchedAnchor?: HTMLElement;

  private userId = 0;

  constructor(
    private store: Store<State>,
    private littleTiffService: LittleTiffService,
    private router: Router,
    private zone: NgZone
  ) {
    super();
    this.subs.push(
      new Subscription(() => this.anchorObserver?.disconnect()),
      this.store.select(s => s.accountState.user).subscribe(user => {
        const userId = user?.id || 0;
        this.canUse = !!user?.permissionIDs?.includes(AppPermission.LittleTiffUse);
        if (userId !== this.userId) {
          this.userId = userId;
          // Drop replies still in flight for the previous user so they never land in the new user's conversation.
          this.userChanged.next();
          this.isLoading = false;
          this.statusRequested = false;
          this.status = null;
          this.messages = this.loadHistory();
          this.loadSavedPosition();
          this.schedulePosition();
        }
      }),
      this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.schedulePosition())
    );
  }

  get hasModels(): boolean {
    return !!this.status?.enabled && !!this.selectedModelId;
  }

  onToggleClick(): void {
    if (this.suppressNextClick) {
      this.suppressNextClick = false;
      return;
    }
    this.toggle();
  }

  onPointerDown(event: PointerEvent): void {
    if (event.button !== 0 || (event.target as HTMLElement).closest('.little-tiff-close')) return;
    const rect = this.root?.nativeElement.getBoundingClientRect();
    if (!rect) return;
    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    if (!this.isDragging &&
      Math.abs(event.clientX - this.drag.startX) < DRAG_THRESHOLD_PX &&
      Math.abs(event.clientY - this.drag.startY) < DRAG_THRESHOLD_PX) return;

    this.isDragging = true;
    this.setPosition(event.clientX - this.drag.offsetX, event.clientY - this.drag.offsetY);
    event.preventDefault();
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    this.drag = null;
    if (!this.isDragging) return;

    this.isDragging = false;
    this.suppressNextClick = true;
    setTimeout(() => (this.suppressNextClick = false));
    if (this.left !== null && this.top !== null) {
      this.savedPosition = { left: this.left, top: this.top };
      try {
        localStorage.setItem(this.positionStorageKey, JSON.stringify(this.savedPosition));
      } catch {
        // localStorage unavailable
      }
    }
  }

  @HostListener('window:resize')
  applyPosition(): void {
    if (this.savedPosition) {
      this.setPosition(this.savedPosition.left, this.savedPosition.top);
      return;
    }

    const size = this.getRootSize();
    const anchor = DEFAULT_ANCHOR_SELECTORS
      .map((selector) => document.querySelector<HTMLElement>(selector))
      .find((el) => !!el && !!el.offsetParent);
    if (anchor) {
      this.watchAnchor(anchor);
      const rect = anchor.getBoundingClientRect();
      this.setPosition(rect.left - size.width - 12, rect.top + rect.height / 2 - size.height / 2);
    } else {
      this.setPosition(window.innerWidth - size.width - 16, VIEWPORT_MARGIN_PX);
    }
  }

  private setPosition(left: number, top: number): void {
    const size = this.getRootSize();
    this.left = Math.min(Math.max(left, VIEWPORT_MARGIN_PX), Math.max(VIEWPORT_MARGIN_PX, window.innerWidth - size.width - VIEWPORT_MARGIN_PX));
    this.top = Math.min(Math.max(top, VIEWPORT_MARGIN_PX), Math.max(VIEWPORT_MARGIN_PX, window.innerHeight - size.height - VIEWPORT_MARGIN_PX));
    // Open the panel on the side with more room and never taller than that room.
    const spaceBelow = window.innerHeight - (this.top + size.height) - 10 - VIEWPORT_MARGIN_PX;
    const spaceAbove = this.top - 10 - VIEWPORT_MARGIN_PX;
    this.panelBelow = spaceBelow >= Math.min(PANEL_HEIGHT_PX, spaceAbove);
    this.panelMaxHeight = Math.max(240, Math.min(PANEL_HEIGHT_PX, this.panelBelow ? spaceBelow : spaceAbove));
    this.panelAlignLeft = this.left + size.width < PANEL_WIDTH_PX + VIEWPORT_MARGIN_PX;
  }

  // Header buttons move as their text, badges and the page scrollbar load, so follow them until the user drags Little Tiff away.
  private watchAnchor(anchor: HTMLElement): void {
    if (this.watchedAnchor === anchor || typeof ResizeObserver === 'undefined') return;
    this.anchorObserver?.disconnect();
    this.watchedAnchor = anchor;
    this.anchorObserver = new ResizeObserver(() => this.zone.run(() => {
      if (!this.savedPosition) this.applyPosition();
    }));
    const toggle = this.root?.nativeElement.querySelector<HTMLElement>('.little-tiff-toggle');
    [document.body, anchor.parentElement, ...Array.from(anchor.parentElement?.children ?? [anchor]), toggle]
      .forEach((el) => el && this.anchorObserver!.observe(el));
  }

  private getRootSize(): { width: number; height: number } {
    const toggle = this.root?.nativeElement.querySelector<HTMLElement>('.little-tiff-toggle');
    const rect = toggle?.getBoundingClientRect();
    return { width: rect?.width || 56, height: rect?.height || 56 };
  }

  private get positionStorageKey(): string {
    return 'littleTiff.position.' + this.userId;
  }

  private loadSavedPosition(): void {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.positionStorageKey) || 'null');
      this.savedPosition = parsed && typeof parsed.left === 'number' && typeof parsed.top === 'number' ? parsed : null;
    } catch {
      this.savedPosition = null;
    }
  }

  private schedulePosition(): void {
    // The header buttons render after navigation, so position once they are on screen.
    setTimeout(() => this.applyPosition(), 300);
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen && !this.statusRequested) {
      this.statusRequested = true;
      this.loadStatus();
    }
    this.scrollToBottom();
  }

  send(): void {
    const text = (this.input || '').trim();
    if (!text || this.isLoading || !this.hasModels) return;

    const currentPath = window.location.pathname || '/';
    const pageTitle = document.title || '';
    const history = this.messages.slice(-MAX_API_HISTORY).map(m => ({ role: m.role, text: m.text }));

    this.input = '';
    this.messages.push({ role: 'user', text });
    this.saveHistory();
    this.isLoading = true;
    this.scrollToBottom();

    this.littleTiffService.ask({
      message: text,
      modelId: this.selectedModelId,
      currentPath,
      pageTitle,
      pageSnapshot: this.buildPageSnapshot(),
      history
    }).pipe(takeUntil(this.userChanged)).subscribe({
      next: (response: LittleTiffAskResponseModel) => {
        this.isLoading = false;
        this.messages.push({
          role: 'assistant',
          text: response.answer || '',
          steps: response.steps || [],
          actions: response.actions || [],
          warnings: response.warnings || [],
          modelId: response.modelId,
          sourcesUsed: response.sourcesUsed || [],
          feedback: this.status?.canGiveFeedback
            ? { question: text, currentPath, pageTitle, contextJson: response.feedbackContextJson || '' }
            : null
        });
        this.saveHistory();
        this.scrollToBottom();
      },
      error: error => {
        this.isLoading = false;
        this.messages.push({ role: 'assistant', text: this.getErrorText(error, 'Little Tiff could not answer right now. Please try again.') });
        this.saveHistory();
        this.scrollToBottom();
      }
    });
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  clearHistory(): void {
    if (!window.confirm('Clear Little Tiff context and start a new conversation?')) return;
    this.messages = [];
    this.saveHistory();
  }

  openAction(action: LittleTiffActionLinkModel): void {
    if (action.url) this.router.navigateByUrl(action.url);
  }

  submitFeedback(message: LittleTiffMessage, rating: 'good' | 'wrong'): void {
    const correction = (message.correction || '').trim();
    if (rating === 'wrong' && !correction) {
      message.feedbackStatus = 'Add the correct answer first.';
      return;
    }

    const feedback = message.feedback;
    if (!feedback) return;

    message.feedbackStatus = 'Saving...';
    this.littleTiffService.saveFeedback({
      rating,
      question: feedback.question,
      answer: message.text,
      correctAnswer: rating === 'wrong' ? correction : null,
      contextJson: feedback.contextJson || null,
      currentPath: feedback.currentPath,
      pageTitle: feedback.pageTitle,
      modelId: message.modelId,
      steps: message.steps,
      actions: message.actions,
      warnings: message.warnings,
      sourcesUsed: message.sourcesUsed
    }).pipe(takeUntil(this.userChanged)).subscribe({
      next: () => {
        message.feedbackSaved = true;
        message.showCorrection = false;
        message.feedbackStatus = 'Feedback saved.';
        this.saveHistory();
      },
      error: error => {
        message.feedbackStatus = this.getErrorText(error, 'Could not save feedback.');
      }
    });
  }

  private loadStatus(): void {
    this.statusMessage = 'Loading models...';
    this.littleTiffService.getStatus().pipe(takeUntil(this.userChanged)).subscribe({
      next: status => {
        this.status = status;
        const models = status.models || [];
        this.selectedModelId =
          models.find(m => m.modelId === this.selectedModelId)?.modelId ||
          models.find(m => m.modelId === status.modelId)?.modelId ||
          models[0]?.modelId ||
          '';
        this.statusMessage = this.hasModels ? '' : 'AI access is unavailable. Ask an administrator to check your AI usage limit.';
      },
      error: error => {
        this.statusRequested = false;
        this.statusMessage = this.getErrorText(error, 'Could not load AI models.');
      }
    });
  }

  // Visible structure of the page (no field values) so answers can name the real buttons and tabs.
  private buildPageSnapshot(): string {
    const sections = [
      { name: 'Headings', selector: 'h1, h2, h3, h4, h5, legend, .p-dialog-title' },
      { name: 'Tabs', selector: '[role="tab"], .nav-tabs a' },
      { name: 'Buttons and links', selector: 'button, a.btn, a.p-button, .p-menuitem-text' },
      { name: 'Field labels', selector: 'label' },
      { name: 'Table columns', selector: 'thead th' }
    ];
    const lines = ['Title: ' + (document.title || '')];

    for (const section of sections) {
      const seen = new Set<string>();
      const items: string[] = [];
      document.querySelectorAll<HTMLElement>(section.selector).forEach(el => {
        if (items.length >= MAX_SNAPSHOT_ITEMS || !el.offsetParent || el.closest('app-little-tiff')) return;
        let text = (el.getAttribute('aria-label') || el.innerText || el.getAttribute('title') || '').replace(/\s+/g, ' ').trim();
        if (text.length > MAX_SNAPSHOT_ITEM_LENGTH) text = text.substring(0, MAX_SNAPSHOT_ITEM_LENGTH) + '...';
        if (!text || seen.has(text.toLowerCase())) return;
        seen.add(text.toLowerCase());
        items.push(text);
      });

      if (items.length) lines.push(section.name + ': ' + items.join(' | '));
    }

    return lines.join('\n');
  }

  // The error interceptor passes server messages through JSON.stringify.
  private getErrorText(error: any, fallback: string): string {
    if (typeof error !== 'string' || !error) return fallback;
    try {
      const parsed = JSON.parse(error);
      return typeof parsed === 'string' ? parsed : parsed?.message || parsed?.title || fallback;
    } catch {
      return error;
    }
  }

  private get storageKey(): string {
    return 'littleTiff.chat.' + this.userId;
  }

  private loadHistory(): LittleTiffMessage[] {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private saveHistory(): void {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.messages.slice(-MAX_STORED_MESSAGES)));
    } catch {
      // sessionStorage full or unavailable
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }
}
